import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import { useTheme } from "@emotion/react";

import {
	fmtDate,
	getCodeDescription,
	getServiceStatus,
	StatusBadge,
	VehicleStatusBadge,
} from "../../utils/functions";
import {
	PATH_ASSET_SENTINEL_EQUIPMENT_DASHBOARD,
	PATH_ASSET_SENTINEL_EQUIPMENT_DISPENSABLE_ITEMS,
	PATH_ASSET_SENTINEL_FLEET_DASHBOARD,
	PATH_ASSET_SENTINEL_FLEET_INSPECTION,
	PATH_ASSET_SENTINEL_FLEET_VEHICLES,
	PATH_ASSET_SENTINEL_FLEET_WORK_ORDERS,
} from "../../utils/routeConstants";
import { getDashboardData } from "../../reducers/DashboardReducer";

const useStyles = makeStyles((theme) => ({
	title: {
		color: theme.palette.text.primary,
		fontWeight: 600,
	},
	colorTitle: {
		color: theme.palette.mode === "dark" ? "#fff" : "#3b3e66",
	},
	cardBackground: {
		background:
			theme.palette.mode === "dark" ? "#1e2e3a" : "rgb(242, 242, 242)",
	},
}));

const Dashboard = () => {
	const classes = useStyles();
	const history = useHistory();
	const theme = useTheme();
	const location = useLocation();
	const dispatch = useDispatch();

	const userAgency = useSelector((state) => state.user.userAgency);
	const { isAuthenticated } = useSelector((state) => state.user);
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const {
		vehicles,
		workOrders,
		employees,
		inspections,
		dispensableItems,
		loading,
	} = useSelector(
		(state) =>
			state?.dashboard || {
				vehicles: [],
				workOrders: [],
				employees: [],
				inspections: [],
				dispensableItems: [],
				loading: false,
			},
	);

	const now = new Date();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const weekAgo = new Date(Date.now() - 7 * 86400000);

	const isFleetDashboard = location.pathname.includes(
		PATH_ASSET_SENTINEL_FLEET_DASHBOARD,
	);
	const isEquipmentDashboard = location.pathname.includes(
		PATH_ASSET_SENTINEL_EQUIPMENT_DASHBOARD,
	);
	const isDashboard = !isFleetDashboard && !isEquipmentDashboard;
	const loadFleet = isDashboard || isFleetDashboard;
	const loadEquipment = isDashboard || isEquipmentDashboard;

	// Fetch dashboard data on mount or when dashboard view changes
	useEffect(() => {
		if (isAuthenticated && userAgency) {
			dispatch(
				getDashboardData(userAgency, {
					loadFleet,
					loadEquipment,
				}),
			);
		}
	}, [dispatch, isAuthenticated, userAgency, loadFleet, loadEquipment]);

	// Get service requirements from dictionary
	const serviceReqs = useMemo(() => {
		return (
			dictionary?.codePerVehicleServiceRequirements?.filter(
				(r) => r.isActive !== false,
			) || []
		);
	}, [dictionary]);

	// FLEET METRICS
	const activeVehicles = useMemo(
		() => vehicles.filter((v) => v.status !== "Retired"),
		[vehicles],
	);

	const inService = useMemo(
		() => vehicles.filter((v) => v["statusInfo.InService"] === true).length,
		[vehicles],
	);

	const outOfService = useMemo(
		() =>
			vehicles.filter((v) => v["statusInfo.InService"] === false).length,
		[vehicles],
	);

	const maintenance = useMemo(
		() => vehicles.filter((v) => v.status === "Maintenance").length,
		[vehicles],
	);

	const readinessPct = useMemo(() => {
		return activeVehicles.length
			? Math.round((inService / activeVehicles.length) * 100)
			: 0;
	}, [activeVehicles.length, inService]);

	// WORK ORDERS
	const openWOs = useMemo(
		() => workOrders.filter((w) => w.status === "Open"),
		[workOrders],
	);

	const closedThisWeek = useMemo(
		() =>
			workOrders.filter(
				(w) =>
					w.status === "Closed" && new Date(w.closedDate) >= weekAgo,
			).length,
		[workOrders, weekAgo],
	);

	const avgWOCost = useMemo(() => {
		return openWOs.length
			? Math.round(
					openWOs.reduce((s, w) => s + (w.cost || 0), 0) /
						openWOs.length,
				)
			: 0;
	}, [openWOs]);

	// INSPECTIONS

	const inspectedVehicleIDs = useMemo(
		() => new Set(inspections.map((i) => i.vehicleId)),
		[inspections],
	);

	const vehiclesEverInspected = useMemo(
		() => activeVehicles.filter((v) => inspectedVehicleIDs.has(v.id)),
		[activeVehicles, inspectedVehicleIDs],
	);

	const inspectedLast24hIDs = useMemo(() => {
		return new Set(
			inspections
				.filter(
					(i) =>
						new Date(i.date) >= new Date(Date.now() - 24 * 3600000),
				)
				.map((i) => i.vehicleId),
		);
	}, [inspections]);

	const todayInsp = useMemo(
		() => inspections.filter((i) => new Date(i.date) >= today),
		[inspections, today],
	);

	const todayPass = useMemo(
		() => todayInsp.filter((i) => i.overallStatus === "Pass").length,
		[todayInsp],
	);

	const todayFail = useMemo(
		() => todayInsp.filter((i) => i.overallStatus === "Fail").length,
		[todayInsp],
	);

	const weekInsp = useMemo(
		() => inspections.filter((i) => new Date(i.date) >= weekAgo),
		[inspections, weekAgo],
	);

	const weekFail = useMemo(
		() => weekInsp.filter((i) => i.overallStatus === "Fail").length,
		[weekInsp],
	);

	const weekPass = useMemo(
		() => weekInsp.filter((i) => i.overallStatus === "Pass").length,
		[weekInsp],
	);

	// Vehicles that have been inspected but NOT in last 24h (need re-inspection)
	const needsReinspection = useMemo(
		() =>
			vehiclesEverInspected.filter(
				(v) =>
					v["statusInfo.InService"] === true &&
					!inspectedLast24hIDs.has(v.id),
			),
		[vehiclesEverInspected, inspectedLast24hIDs],
	);

	// Vehicles with a failed inspection in the last 7 days (no subsequent pass)
	const failedVehicles = useMemo(() => {
		const result = [];
		vehiclesEverInspected.forEach((v) => {
			const vInsp = inspections
				.filter((i) => i.vehicleId === v.id)
				.sort((a, b) => new Date(b.date) - new Date(a.date));
			if (
				vInsp.length &&
				vInsp[0].overallStatus === "Fail" &&
				new Date(vInsp[0].date) >= weekAgo
			) {
				result.push({ vehicle: v, inspection: vInsp[0] });
			}
		});
		return result;
	}, [vehiclesEverInspected, inspections, weekAgo]);

	// Recent inspections (last 7 days, vehicles that have been inspected only)
	const recentInspections = useMemo(() => {
		return weekInsp
			.filter((i) => inspectedVehicleIDs.has(i.vehicleId))
			.sort((a, b) => new Date(b.date) - new Date(a.date))
			.slice(0, 6);
	}, [weekInsp, inspectedVehicleIDs]);

	// SERVICE DUE

	const serviceDueItems = useMemo(() => {
		const items = [];
		activeVehicles
			.filter((v) => (v.serviceRequirements || []).length > 0)
			.forEach((v) => {
				(v.serviceRequirements || []).forEach((reqID) => {
					const req = serviceReqs.find((r) => r.ptsCodeId === reqID);
					if (!req) return;
					const s = getServiceStatus(v, req, workOrders);
					if (s.status === "Overdue" || s.status === "Due Soon") {
						items.push({ vehicle: v, req, ...s });
					}
				});
			});
		items.sort((a, b) => {
			const order = { Overdue: 0, "Due Soon": 1 };
			return (
				(order[a.status] ?? 2) - (order[b.status] ?? 2) ||
				a.dueIn - b.dueIn
			);
		});
		return items;
	}, [activeVehicles, serviceReqs, workOrders]);

	const overdueServiceCount = useMemo(
		() => serviceDueItems.filter((i) => i.status === "Overdue").length,
		[serviceDueItems],
	);

	// DISPENSABLE ITEMS

	const dispNeedRestock = useMemo(
		() =>
			dispensableItems.filter(
				(i) =>
					(Number(i.unitsInStock) || 0) <=
					(Number(i.minBeforeRestock) || 0),
			),
		[dispensableItems],
	);

	const dispOutOfStock = useMemo(
		() =>
			dispensableItems.filter((i) => (Number(i.unitsInStock) || 0) === 0),
		[dispensableItems],
	);

	const dispTotalEstCost = useMemo(() => {
		return dispNeedRestock.reduce((s, i) => {
			const stock = Number(i.unitsInStock) || 0;
			const max = Number(i.maxStock) || 0;
			return i.cost != null && max > 0
				? s + Math.max(0, max - stock) * +i.cost
				: s;
		}, 0);
	}, [dispNeedRestock]);

	// HELPER FUNCTIONS

	const getEmployeeDisplay = (employeeId) => {
		const emp = employees.find((e) => e.id === employeeId);
		return emp ? `${emp.rank} ${emp.lastName}` : "—";
	};

	// Show loading state
	if (loading) {
		return (
			<div className="asset-page">
				<h5 className={classes.title}>Dashboard</h5>
				<div
					style={{
						textAlign: "center",
						padding: "40px",
						color: "var(--text2)",
					}}
				>
					Loading dashboard data...
				</div>
			</div>
		);
	}

	return (
		<div className="asset-page">
			<>
				<h5 className={classes.title}>Dashboard</h5>
				{/* KPI CARDS */}
				<div className="kpi-grid">
					{(isDashboard || isFleetDashboard) && (
						<>
							{/* In Service */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									inService === activeVehicles.length
										? "green"
										: inService >=
											  activeVehicles.length * 0.75
											? "accent"
											: "red"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_FLEET_VEHICLES,
									)
								}
							>
								<div className="kpi-label">In Service</div>
								<div className="kpi-value">{inService}</div>
								<div className="kpi-sub">
									{outOfService} out · {maintenance}{" "}
									maintenance
								</div>
								<div className="kpi-trend">🚓</div>
							</div>

							{/* Fleet Readiness */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									readinessPct >= 75
										? "green"
										: readinessPct >= 50
											? "yellow"
											: "red"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_FLEET_VEHICLES,
									)
								}
							>
								<div className="kpi-label">Fleet Readiness</div>
								<div className="kpi-value">{readinessPct}%</div>
								<div className="kpi-sub">
									of {activeVehicles.length} active vehicles
								</div>
								<div className="kpi-trend">📊</div>
							</div>

							{/* Work Orders */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									openWOs.length === 0
										? "green"
										: openWOs.length <= 3
											? "yellow"
											: "red"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_FLEET_WORK_ORDERS,
									)
								}
							>
								<div className="kpi-label">
									Open Work Orders
								</div>
								<div className="kpi-value">
									{openWOs.length}
								</div>
								<div className="kpi-sub">
									{closedThisWeek} closed this week
									{avgWOCost > 0
										? ` · avg $${avgWOCost.toFixed(2).toLocaleString()}`
										: ""}
								</div>
								<div className="kpi-trend">🔧</div>
							</div>

							{/* Inspections Today */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									todayFail > 0
										? "red"
										: todayInsp.length > 0
											? "green"
											: "blue"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_FLEET_INSPECTION,
									)
								}
							>
								<div className="kpi-label">
									Inspections Today
								</div>
								<div className="kpi-value">
									{todayInsp.length}
								</div>
								<div className="kpi-sub">
									{todayPass} pass · {todayFail} fail
								</div>
								<div className="kpi-trend">✅</div>
							</div>

							{/* Inspection Issues */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									failedVehicles.length > 0
										? "red"
										: needsReinspection.length > 0
											? "yellow"
											: "green"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_FLEET_INSPECTION,
									)
								}
							>
								<div className="kpi-label">
									Inspection Issues
								</div>
								<div className="kpi-value">
									{failedVehicles.length +
										needsReinspection.length}
								</div>
								<div className="kpi-sub">
									{failedVehicles.length} failed ·{" "}
									{needsReinspection.length} need re-check
								</div>
								<div className="kpi-trend">⚠️</div>
							</div>
						</>
					)}

					{(isDashboard || isEquipmentDashboard) && (
						<>
							{/* Restock Needed */}
							<div
								className={`kpi-card ${classes.cardBackground} ${classes.colorTitle} clickable ${
									dispOutOfStock.length > 0
										? "red"
										: dispNeedRestock.length > 0
											? "yellow"
											: "green"
								}`}
								onClick={() =>
									history.push(
										PATH_ASSET_SENTINEL_EQUIPMENT_DISPENSABLE_ITEMS,
									)
								}
							>
								<div className="kpi-label">Restock Needed</div>
								<div className="kpi-value">
									{dispNeedRestock.length}
								</div>
								<div className="kpi-sub">
									{dispOutOfStock.length > 0
										? `${dispOutOfStock.length} out of stock · `
										: ""}
									{dispensableItems.length} total items
								</div>
								<div className="kpi-trend">📦</div>
							</div>
						</>
					)}
				</div>

				{/* FLEET DASHBOARD CONTENT */}

				{(isDashboard || isFleetDashboard) && (
					<>
						{/* Alerts */}
						{failedVehicles.length > 0 && (
							<div
								className="alert-bar"
								style={{
									marginBottom: 12,
									borderColor: "var(--red)",
									color: "var(--red)",
									background: "rgba(239,68,68,0.08)",
								}}
							>
								🚫 {failedVehicles.length} vehicle
								{failedVehicles.length > 1
									? "s have"
									: " has"}{" "}
								a failed inspection this week with no subsequent
								pass
							</div>
						)}
						{needsReinspection.length > 0 && (
							<div
								className="alert-bar"
								style={{ marginBottom: 12 }}
							>
								⚠️ {needsReinspection.length} in-service vehicle
								{needsReinspection.length > 1 ? "s" : ""} not
								inspected in 24h
							</div>
						)}
						{overdueServiceCount > 0 && (
							<div
								className="alert-bar"
								style={{
									marginBottom: 20,
									borderColor: "var(--yellow)",
									color: "var(--yellow)",
									background: "rgba(234,179,8,0.08)",
								}}
							>
								🔧 {overdueServiceCount} vehicle service
								requirement
								{overdueServiceCount > 1 ? "s are" : " is"}{" "}
								overdue
							</div>
						)}

						{/* Main Grid: Fleet Status by Division + Vehicles Needing Attention */}
						<div className="dash-grid" style={{ marginBottom: 20 }}>
							<div className="dash-panel">
								<div className="dash-panel-header">
									<span
										className={`${classes.colorTitle} dash-panel-title`}
									>
										Fleet Status by Division
									</span>
								</div>
								{[
									...new Set(
										activeVehicles.map((v) => v.division),
									),
								]
									.filter(Boolean)
									.sort()
									.map((div) => {
										const divV = activeVehicles.filter(
											(v) => v.division === div,
										);
										const divIn = divV.filter(
											(v) =>
												v["statusInfo.InService"] ===
												true,
										).length;
										const divMaint = divV.filter(
											(v) => v.status === "Maintenance",
										).length;
										const divOut = divV.filter(
											(v) =>
												v["statusInfo.InService"] ===
												false,
										).length;
										const pct = divV.length
											? Math.round(
													(divIn / divV.length) * 100,
												)
											: 0;
										const cls =
											pct >= 75
												? "high"
												: pct >= 50
													? "mid"
													: "low";
										return (
											<div
												key={div}
												onClick={() =>
													history.push(
														PATH_ASSET_SENTINEL_FLEET_VEHICLES,
													)
												}
												style={{
													padding: "10px 18px",
													borderBottom:
														"1px solid var(--border)",
													cursor: "pointer",
												}}
											>
												<div
													style={{
														display: "flex",
														justifyContent:
															"space-between",
														marginBottom: 6,
													}}
												>
													<span
														style={{
															fontSize: 13,
															fontWeight: 500,
															color:
																theme.palette
																	.mode ===
																"dark"
																	? "#fff"
																	: "#000",
														}}
													>
														{getCodeDescription(
															div,
															dictionary.codePerDepartment,
														)}
													</span>
													<div
														style={{
															display: "flex",
															gap: 10,
															fontSize: 11,
															fontFamily:
																"var(--mono)",
														}}
													>
														<span
															style={{
																color: "var(--green)",
															}}
														>
															{divIn} in svc
														</span>
														{divMaint > 0 && (
															<span
																style={{
																	color: "var(--yellow)",
																}}
															>
																{divMaint} maint
															</span>
														)}
														{divOut > 0 && (
															<span
																style={{
																	color: "var(--red)",
																}}
															>
																{divOut} out
															</span>
														)}
													</div>
												</div>
												<div className="readiness-bar">
													<div
														className={`readiness-fill ${cls}`}
														style={{
															width: `${pct}%`,
														}}
													/>
												</div>
											</div>
										);
									})}
							</div>

							<div className="dash-panel">
								<div className="dash-panel-header">
									<span
										className={`${classes.colorTitle} dash-panel-title`}
									>
										Vehicles Needing Attention
									</span>
									{failedVehicles.length +
										needsReinspection.length ===
									0 ? (
										<span className="svc-badge ok">
											All Clear
										</span>
									) : (
										<span className="svc-badge overdue">
											{failedVehicles.length +
												needsReinspection.length}{" "}
											vehicles
										</span>
									)}
								</div>
								{failedVehicles.length === 0 &&
								needsReinspection.length === 0 ? (
									<div className="empty-state">
										<div className="empty-text">
											All inspected vehicles are current
										</div>
									</div>
								) : (
									<>
										{failedVehicles.map(
											({ vehicle, inspection }) => (
												<div
													className="dash-row"
													key={vehicle.id}
													onClick={() =>
														history.push(
															PATH_ASSET_SENTINEL_FLEET_VEHICLES,
														)
													}
													style={{
														borderLeft:
															"3px solid var(--red)",
														paddingLeft: 10,
														cursor: "pointer",
														color:
															theme.palette
																.mode === "dark"
																? "#fff"
																: "#000",
													}}
												>
													<div>
														<div
															className="dash-row-label"
															style={{
																color: "var(--red)",
															}}
														>
															🚫{" "}
															{vehicle.unitNumber}{" "}
															— {vehicle.make}{" "}
															{vehicle.model}
														</div>
														<div className="dash-row-sub">
															Failed{" "}
															{inspection.type} ·{" "}
															{fmtDate(
																inspection.date,
															)}{" "}
															·{" "}
															{
																inspection.responses.filter(
																	(r) =>
																		r.status ===
																		"Fail",
																).length
															}{" "}
															item
															{inspection.responses.filter(
																(r) =>
																	r.status ===
																	"Fail",
															).length !== 1
																? "s"
																: ""}{" "}
															failed
														</div>
													</div>
													<VehicleStatusBadge
														status={
															vehicle[
																"statusInfo.Description"
															]
														}
														inService={
															vehicle[
																"statusInfo.InService"
															]
														}
													/>
												</div>
											),
										)}
										{needsReinspection
											.slice(0, 4)
											.map((v) => (
												<div
													className="dash-row"
													key={v.id}
													onClick={() =>
														history.push(
															PATH_ASSET_SENTINEL_FLEET_VEHICLES,
														)
													}
													style={{
														borderLeft:
															"3px solid var(--yellow)",
														paddingLeft: 10,
														cursor: "pointer",
														color:
															theme.palette
																.mode === "dark"
																? "#fff"
																: "#000",
													}}
												>
													<div>
														<div className="dash-row-label">
															⚠️ {v.unitNumber} —{" "}
															{v.make} {v.model}
														</div>
														<div className="dash-row-sub">
															Not inspected in 24h
															·{" "}
															{getCodeDescription(
																v.division,
																dictionary.codePerDepartment,
															)}
														</div>
													</div>
													<VehicleStatusBadge
														status={
															v[
																"statusInfo.Description"
															]
														}
														inService={
															v[
																"statusInfo.InService"
															]
														}
													/>
												</div>
											))}
									</>
								)}
							</div>
						</div>

						{/* Service Due */}
						<div
							className="dash-panel"
							style={{ marginBottom: 20 }}
						>
							<div className="dash-panel-header">
								<span
									className={`${classes.colorTitle} dash-panel-title`}
								>
									Service Due
								</span>
								{serviceDueItems.length > 0 ? (
									<span
										className="svc-badge overdue"
										style={{ fontSize: 11 }}
									>
										{overdueServiceCount} Overdue ·{" "}
										{serviceDueItems.length -
											overdueServiceCount}{" "}
										Due Soon
									</span>
								) : (
									<span className="svc-badge ok">
										All Current
									</span>
								)}
							</div>
							{serviceDueItems.length === 0 ? (
								<div className="empty-state">
									<div className="empty-text">
										All vehicle service requirements are
										current
									</div>
								</div>
							) : (
								serviceDueItems.slice(0, 8).map((item, i) => (
									<div
										className="svc-row"
										key={i}
										onClick={() =>
											history.push(
												PATH_ASSET_SENTINEL_FLEET_VEHICLES,
											)
										}
										style={{
											cursor: "pointer",
											color:
												theme.palette.mode === "dark"
													? "#fff"
													: "#000",
										}}
									>
										<div className="svc-row-left">
											<div className="svc-row-label">
												{item.vehicle.unitNumber} —{" "}
												{item.vehicle.year}{" "}
												{item.vehicle.make}{" "}
												{item.vehicle.model}
											</div>
											<div className="svc-row-sub">
												{getCodeDescription(
													item.req.Code,
													dictionary.codePerVehicleServiceRequirements,
												)}
												{item.lastServiceDate
													? ` · Last: ${fmtDate(item.lastServiceDate)}`
													: " · Never serviced"}
											</div>
										</div>
										<div className="svc-row-right">
											<span
												className={`svc-due-num ${item.status === "Overdue" ? "overdue" : "due-soon"}`}
											>
												{item.status === "Overdue"
													? item.unit === "mi"
														? `${Math.abs(item.dueIn).toLocaleString()} mi overdue`
														: `${Math.abs(item.dueIn)}d overdue`
													: item.unit === "mi"
														? `${item.dueIn.toLocaleString()} mi left`
														: `${item.dueIn}d left`}
											</span>
											<span
												className={`svc-badge ${item.status === "Overdue" ? "overdue" : "due-soon"}`}
											>
												{item.status}
											</span>
										</div>
									</div>
								))
							)}
						</div>

						{/* Open WOs + Recent Inspections */}
						<div className="dash-grid" style={{ marginBottom: 20 }}>
							<div className="dash-panel">
								<div className="dash-panel-header">
									<span
										className={`${classes.colorTitle} dash-panel-title`}
									>
										Open Work Orders
									</span>
									<span
										style={{
											fontSize: 11,
											fontFamily: "var(--mono)",
											color: "var(--text2)",
										}}
									>
										{openWOs.length} open
									</span>
								</div>
								{openWOs.length === 0 ? (
									<div className="empty-state">
										<div className="empty-text">
											No open work orders
										</div>
									</div>
								) : (
									openWOs.slice(0, 6).map((wo) => {
										const veh = vehicles.find(
											(v) => v.id === wo.vehicleId,
										);
										const daysOpen = Math.round(
											(now - new Date(wo.openedDate)) /
												86400000,
										);
										return (
											<div
												className="dash-row"
												key={wo.id}
												onClick={() =>
													history.push(
														PATH_ASSET_SENTINEL_FLEET_VEHICLES,
													)
												}
												style={{
													cursor: "pointer",
													color:
														theme.palette.mode ===
														"dark"
															? "#fff"
															: "#000",
												}}
											>
												<div>
													<div className="dash-row-label">
														{veh?.unitNumber} —{" "}
														{veh?.make} {veh?.model}
													</div>
													<div className="dash-row-sub">
														{wo.notes?.slice(0, 55)}
														{wo.notes?.length > 55
															? "…"
															: ""}
													</div>
												</div>
												<div
													style={{
														textAlign: "right",
														flexShrink: 0,
													}}
												>
													<StatusBadge
														status={wo.type}
													/>
													<div
														style={{
															fontSize: 10,
															color:
																daysOpen > 7
																	? "var(--red)"
																	: "var(--text3)",
															fontFamily:
																"var(--mono)",
															marginTop: 3,
														}}
													>
														{daysOpen}d open
													</div>
												</div>
											</div>
										);
									})
								)}
							</div>

							<div className="dash-panel">
								<div className="dash-panel-header">
									<span
										className={`${classes.colorTitle} dash-panel-title`}
									>
										Recent Inspections (7 days)
									</span>
									<span
										style={{
											fontSize: 11,
											fontFamily: "var(--mono)",
											color: "var(--text2)",
										}}
									>
										<span style={{ color: "var(--green)" }}>
											{weekPass}✓
										</span>
										{weekFail > 0 && (
											<span
												style={{
													color: "var(--red)",
													marginLeft: 6,
												}}
											>
												{weekFail}✗
											</span>
										)}
									</span>
								</div>
								{recentInspections.length === 0 ? (
									<div className="empty-state">
										<div className="empty-text">
											No inspections in the past 7 days
										</div>
									</div>
								) : (
									recentInspections.map((insp) => {
										const veh = vehicles.find(
											(v) => v.id === insp.vehicleId,
										);
										const fails = insp.responses.filter(
											(r) => r.status === "Fail",
										).length;
										return (
											<div
												className="dash-row"
												key={insp.id}
												onClick={() =>
													history.push(
														PATH_ASSET_SENTINEL_FLEET_VEHICLES,
													)
												}
												style={{
													cursor: "pointer",
													color:
														theme.palette.mode ===
														"dark"
															? "#fff"
															: "#000",
												}}
											>
												<div>
													<div className="dash-row-label">
														{veh?.unitNumber} —{" "}
														{veh?.make} {veh?.model}
													</div>
													<div className="dash-row-sub">
														{getCodeDescription(
															insp.type,
															dictionary.codePerInspectionTypes,
														)}{" "}
														·{" "}
														{getEmployeeDisplay(
															insp.perPersonnelId,
														)}{" "}
														· {fmtDate(insp.date)}
														{fails > 0 && (
															<span
																style={{
																	color: "var(--red)",
																	marginLeft: 6,
																}}
															>
																{fails} failed
															</span>
														)}
													</div>
												</div>
												<span style={{ fontSize: 16 }}>
													{insp.overallStatus ===
													"Pass"
														? "✅"
														: insp.overallStatus ===
															  "Fail"
															? "🚫"
															: "⚠️"}
												</span>
											</div>
										);
									})
								)}
							</div>
						</div>
					</>
				)}

				{/* EQUIPMENT DASHBOARD CONTENT */}

				{(isDashboard || isEquipmentDashboard) && (
					<div className="dash-panel" style={{ marginTop: 16 }}>
						<div className="dash-panel-header">
							<span
								className={`${classes.colorTitle} dash-panel-title`}
							>
								📦 Dispensable Items — Restock Needed
							</span>
							<div
								style={{
									display: "flex",
									gap: 8,
									alignItems: "center",
								}}
							>
								{dispOutOfStock.length > 0 && (
									<span
										className="svc-badge overdue"
										style={{ fontSize: 11 }}
									>
										{dispOutOfStock.length} out of stock
									</span>
								)}
								<span
									className="svc-badge warning"
									style={{ fontSize: 11 }}
								>
									{dispNeedRestock.length} item
									{dispNeedRestock.length !== 1 ? "s" : ""}
								</span>
								{dispTotalEstCost > 0 && (
									<span
										style={{
											fontFamily: "var(--mono)",
											fontSize: 11,
											color: "var(--text2)",
										}}
									>
										Est. $
										{Number(
											dispTotalEstCost,
										).toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</span>
								)}
							</div>
						</div>
						{dispNeedRestock.length === 0 ? (
							<div className="empty-state">
								<div className="empty-text">
									All items are well-stocked
								</div>
							</div>
						) : (
							<>
								{dispNeedRestock.slice(0, 6).map((item) => {
									const stock =
										Number(item.unitsInStock) || 0;
									const max = Number(item.maxStock) || 0;
									const need =
										max > 0
											? Math.max(0, max - stock)
											: null;
									const isOut = stock === 0;
									return (
										<div
											className="dash-row"
											key={item.id}
											onClick={() =>
												history.push(
													PATH_ASSET_SENTINEL_EQUIPMENT_DISPENSABLE_ITEMS,
												)
											}
											style={{
												cursor: "pointer",
												color:
													theme.palette.mode ===
													"dark"
														? "#fff"
														: "#000",
											}}
										>
											<div>
												<div className="dash-row-label">
													<span
														className="mono"
														style={{
															fontSize: 11,
															color: "var(--accent)",
															marginRight: 6,
														}}
													>
														{item.trackingNumber}
													</span>
													{item.description}
												</div>
												<div className="dash-row-sub">
													{item.vendor
														? `${item.vendor} · `
														: ""}
													In stock:{" "}
													<strong
														style={{
															color: isOut
																? "var(--red)"
																: "var(--yellow)",
														}}
													>
														{stock}
													</strong>
													{item.minBeforeRestock !=
													null
														? ` / min ${item.minBeforeRestock}`
														: ""}
													{need != null
														? ` · Order: ${need}`
														: ""}
													{item.cost != null &&
													need != null &&
													need > 0
														? ` · Est. $${(need * +item.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
														: ""}
												</div>
											</div>
											<span
												style={{
													fontSize: 11,
													fontFamily: "var(--mono)",
													color: isOut
														? "var(--red)"
														: "var(--yellow)",
													fontWeight: 700,
													flexShrink: 0,
												}}
											>
												{isOut
													? "OUT"
													: `${stock} left`}
											</span>
										</div>
									);
								})}
								{dispNeedRestock.length > 6 && (
									<div
										style={{
											padding: "8px 14px",
											fontSize: 11,
											color: "var(--text3)",
											borderTop:
												"1px solid var(--border)",
											textAlign: "center",
											cursor: "pointer",
										}}
										onClick={() =>
											history.push(
												PATH_ASSET_SENTINEL_EQUIPMENT_DISPENSABLE_ITEMS,
											)
										}
									>
										+{dispNeedRestock.length - 6} more items
										need restocking — click to view all
									</div>
								)}
							</>
						)}
					</div>
				)}
			</>
		</div>
	);
};

export default Dashboard;
